<template>
  <section class="renewal-view">
    <div class="renewal-view__rc-info">
      <div class="icon">
        <UserIcon />
      </div>
      <DropDownList
        v-if="canEmulateRc"
        class="rc-dropdown"
        :dataItems="rcList"
        v-model="selectedRc"
        text-field="FullName"
        data-item-key="PersonJobId"
        required
      />
      <div v-else class="name">{{ stats?.RcName }}</div>
    </div>
    <div v-if="stats" class="renewal-view__chart">
      <header>Companies up for Renewal</header>
      <div class="info">Click to Filter/Reset</div>
      <div class="chart">
        <div
          v-for="(intervalCompanies, interval) in stats.RenewalIntervals"
          :key="interval"
          :class="
            `level-${interval}` +
            (selectedInterval == interval ? ' active' : '')
          "
          @click="renewalItemClicked(interval)"
        >
          <div
            class="bar-fill"
            :style="`height:${
              138 * (intervalCompanies / stats.TotalCompanies) +
              (intervalCompanies ? 12 : 0)
            }px`"
          ></div>
          <span class="value">{{ intervalCompanies }}</span>
        </div>
      </div>
      <footer>
        <div class="chart-footer">
          <div>
            {{ moment().subtract(1, "month").startOf("month").format("MMMM") }}
          </div>
          <div>{{ moment().startOf("month").format("MMMM") }}</div>
          <div>
            {{ moment().add(1, "month").startOf("month").format("MMMM") }}
          </div>
          <div>
            {{ moment().add(2, "month").startOf("month").format("MMMM") }}
          </div>
        </div>
      </footer>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, watchEffect } from "vue";
import UserIcon from "@/icons/UserIcon.vue";
import type { MiniCrmRenewal } from "@/Domain/MiniCrmRenewal";
import moment from "moment";
import { useMainStore } from "@/stores/useMainStore";
import { UserRoles } from "@/Domain/UserRoles";
import type { MiniCrmPerson } from "@/Domain/MiniCrmPerson";
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { useMiniCrmStore } from "@/stores/useMiniCrmStore";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import { EnumJobFunction } from "@/Domain/EnumJobFunction";

const main = useMainStore();
const crmStore = useMiniCrmStore();

const props = defineProps<{
  stats?: MiniCrmRenewal;
}>();

const emit = defineEmits<{
  (e: "intervalChanged", interval: number): void;
}>();

const selectedInterval = ref<number>(-1);
const rcList = ref<MiniCrmPerson[]>([]);

const selectedRc = ref<MiniCrmPerson>({
  FullName: props.stats?.RcName ?? "",
  PersonJobId: 0,
} as MiniCrmPerson);

const renewalItemClicked = (interval: number) => {
  if (interval === selectedInterval.value) {
    selectedInterval.value = -1;
  } else {
    selectedInterval.value = interval;
  }
  emit("intervalChanged", selectedInterval.value);
};

const canEmulateRc = computed(() => {
  return (
    main.isInAnyRole([
      UserRoles.ROLE_MIS,
      UserRoles.ROLE_IN_HOUSE_MIS_MANAGER,
      UserRoles.ROLE_MANAGEMENT,
    ]) || isGroupLeaderAndNotManagement.value
  );
});

const isGroupLeaderAndNotManagement = computed(() => {
  return (
    main.isInRole(UserRoles.ROLE_GROUP_LEADER) &&
    !main.isInAnyRole([
      UserRoles.ROLE_MIS,
      UserRoles.ROLE_IN_HOUSE_MIS_MANAGER,
      UserRoles.ROLE_MANAGEMENT,
    ])
  );
});

const getRcListAsync = async function () {
  let apiPromise = null;

  if (isGroupLeaderAndNotManagement.value) {
    apiPromise = api.get("VueGetAssociatedPeopleCrm", {
      userLogin: main.userLogin,
      role: EnumJobFunction.RabbinicCoordinator,
    });
  } else {
    apiPromise = api.get("GetCrmRcList", {
      userLogin: main.userLogin,
    });
  }

  return handleResponsePromiseAsync(
    apiPromise,
    (data) => {
      rcList.value = data;
      if (selectedRc.value.PersonJobId == 0) {
        rcList.value.push(selectedRc.value);
      }
    },
    "MiniCrm.vue",
    "getRcListAsync"
  );
};

watchEffect(() => {
  if (selectedRc.value.PersonJobId == 0) {
    selectedRc.value.FullName = props.stats?.RcName ?? "";
  }
});

watchEffect(() => {
  crmStore.emulatedPersonJobId = selectedRc.value.PersonJobId;
});

onBeforeMount(async () => {
  if (canEmulateRc.value) await getRcListAsync();
});
</script>

<style scoped lang="scss">
.renewal-view {
  &__rc-info {
    display: flex;
    gap: 12px;

    .icon {
      background: #325ea3;
      width: 34px;
      height: 34px;
      border-radius: 4px;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .name {
      color: #1f262e;
      font-size: 18px;
      font-weight: 700;
      line-height: 20px;
    }

    .companies {
      color: #9fa4aa;
      font-size: 14px;
    }
  }

  .rc-dropdown {
    display: flex;
    flex: 1;
  }

  &__chart {
    background: white;
    border: 1px solid #eeeff0;
    padding: 24px 0 16px;
    text-align: center;
    border-radius: 4px;
    margin-top: 24px;

    > header {
      color: #1f262e;
      font-size: 16px;
      font-weight: 700;
      line-height: 22px;
    }

    .info {
      color: #9fa4aa;
      font-size: 14px;
      display: block;
    }

    .chart {
      display: flex;
      gap: 16px;
      margin: 24px 16px 16px;
      .value {
        position: relative;
      }
      > div {
        color: white;
        flex: 1;
        height: 150px;
        border-radius: 12px;
        position: relative;
        background: #999;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        cursor: pointer;
        font-weight: bold;
        margin-top: 0;
        transition: all 0.3s ease-in-out;
        z-index: 1;

        .bar-fill {
          position: absolute;
          width: 100%;
          bottom: 0;
          left: 0;
          border-radius: 12px;
          z-index: -1;
        }

        &.level-1 .bar-fill {
          background: #ab1400;
        }

        &.level-2 .bar-fill {
          background: #fd6150;
        }

        &.level-3 .bar-fill {
          //background: #ff9d42;
          background: #fecc4b;
        }

        &.level-4 .bar-fill {
          //background: #fecc4b;
          background: #008b38;
        }

        &.level-5 .bar-fill {
          background: #008b38;
        }

        &:hover {
          margin-top: -5px;
        }

        &.active {
          outline: 4px solid #325ea3;
        }
      }
    }
    > footer .chart-footer {
      display: flex;
      gap: 16px;
      margin: 5px 16px 0px;

      > div {
        flex: 1;
        font-size: 14px;
        color: #4e535a;
      }
    }
  }
}
</style>
